import { Grid, Typography } from '@mui/material';
import { ActionButton } from '@tassat/button';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { AccountClientGroupRecord } from '@tassat/fred-definitions';
import { Delete as DeleteIcon, Edit as EditIcon } from '@tassat/icon';
import { formatEpochDate } from '@tassat/utility';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import history from 'Lib/browserHistory';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getOpenAccountsArray } from 'Selectors/accounts';
import { getAcgById } from 'Selectors/acg';
import { getAcgUsersbyACGID } from 'Selectors/acgUsers';

// import ACGNumberUsersAgGrid from '../ACGNumberUserGrid';

import type { ACGDetailsProps } from '.';

export function ACGDetails(props: ACGDetailsProps) {
    const {
        classes,
        match,
        editACG
    } = props;
    const acgId = match.params.acgID;
    const acgObj = useAppSelector((state) => getAcgById(state, acgId));
    const accounts = useAppSelector((state) => getOpenAccountsArray(state));
    const acgUsers = useAppSelector((state) => getAcgUsersbyACGID(state, undefined, false, acgObj?.ACGId));
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const deleteACGNumberValidation = (acg: AccountClientGroupRecord): boolean => {
        const id = acg.ACGId;

        const mapped = accounts.find(acc => (acc.ACGId1 === id || acc.ACGId2 === id || acc.ACGId3 === id) &&
            !acc.IsDeleted);

        const mappedUsers = acgUsers.filter(acgUser => !acgUser.acgUser.IsDeleted && acgUser.acgUser.ACGId === id);

        if (mapped) {
            const validationFailedAction: ConfirmationWorkflowAction = {
                type: 'VALIDATION_FAILED',
                workflowConfiguration: {
                    validationTitle: 'Validation Error',
                    validationMessage: 'ACG Number is mapped to an account, please remove mapping before deleting.'
                }
            };
            dispatchConfirmationWorkflowAction(validationFailedAction);

            return true;
        } else {
            const validationSuccessfulAction: ConfirmationWorkflowAction = {
                type: 'VALIDATION_SUCCESS',
                workflowConfiguration: {
                    selectedItem: acg,
                    actionType: ActionTypes.delete,
                    confirmTitle: `Confirm Deleting ACG Number ${acg.ACGNumber} - ${acg.ACGName}`,
                    confirmBody: `Please confirm deleting ACG Number ${acg.ACGNumber}${mappedUsers.length > 0 ? `\nWARNING: There ${mappedUsers.length === 1 ? 'is 1 User' : `are ${mappedUsers.length} Users`} linked to this ACG Number.` : ''}`
                }
            };
            dispatchConfirmationWorkflowAction(validationSuccessfulAction);
        }

        return false;
    };

    const deleteACGNumber = async (acg: AccountClientGroupRecord) => {
        try {
            const sendValues = { ...acg };

            const response = await Worker.getInstance()
                .deleteAcg(sendValues.ACGId);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Deleted Account Client Group Number ${acg.ACGNumber}`,
                        successBody: `Successfully deleted the Account Client Group Number ${acg.ACGNumber}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
                history.push('/admin/acg');
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete Account Client Group Number ${acg.ACGNumber}`,
                        failureBody: `Could not delete the Account Client Group Number ${acg.ACGNumber}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Delete Account Client Group Number ${acg.ACGNumber}`,
                    failureBody: `Something went wrong deleting the Account Client Group Number ${acg.ACGNumber}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: acgObj,
                setSelectedItem: () => acgObj,
                preActionValidationAndConfig: { [ActionTypes.delete]: deleteACGNumberValidation },
                onConfirm: { [ActionTypes.delete]: deleteACGNumber },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    if (!acgObj) return <div />;

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                alignItems='flex-start'
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    alignItems='flex-start'
                    className={classes.acgContainer}
                    container
                    direction='row'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.acgIdContainer}
                        container
                        item
                        xs={1}
                    >
                        <div
                            className={classes.acgIdBox}
                            id='acgNumberDetailsAcgNumberValue'
                        >{acgObj.ACGNumber.toUpperCase()}
                        </div>
                    </Grid>
                    <Grid
                        alignItems='flex-start'
                        container
                        direction='column'
                        item
                        wrap='nowrap'
                        xs={10}
                    >
                        <Grid
                            alignItems='center'
                            container
                            direction='row'
                            spacing={0}
                        >
                            <Typography
                                className={classes.acgName}
                                id='acgNumberDetailsAcgNameValue'
                                variant='h4'
                            >
                                <strong>{acgObj.ACGName}</strong>
                            </Typography>
                        </Grid>
                        <Grid
                            className={classes.detailsContainer}
                            container
                            direction='column'
                            spacing={0}
                        >
                            <Typography
                                className={classes.subText}
                                id='acgNumberDetailsCreatedTimeValue'
                                variant='body2'
                            >
                                {`Created: ${formatEpochDate(acgObj.CreatedAt)} by `} <span>{acgObj.CreatedByUsername}</span>
                            </Typography>
                            <Typography
                                className={classes.subText}
                                id='acgNumberDetailsModifiedTimeValue'
                                variant='body2'
                            >
                                {`Last Modified: ${formatEpochDate(acgObj.UpdatedAt)} by`} <span>{acgObj.ModifiedByUsername}</span>
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        direction='row'
                        item
                        wrap='nowrap'
                        xs={1}
                    >
                        <ActionButton
                            className={classes.actionButton}
                            handleClick={() => deleteACGNumberValidation(acgObj)}
                            Icon={DeleteIcon}
                            id='acgNumberDetailsDeleteAcgButton'
                            text='Delete'
                            type='icon'
                        />
                        <ActionButton
                            className={classes.actionButton}
                            handleClick={() => editACG(acgObj)}
                            Icon={EditIcon}
                            id='acgNumberDetailsEditAcgButton'
                            text='Edit'
                            type='icon'
                        />
                    </Grid>
                </Grid>
                {/* <Grid
                    className={classes.gridContainer}
                    container
                    direction='column'
                >
                    <Grid item >
                        <Typography
                            className={classes.gridTitleText}
                            variant='h3'
                        >
                            ACG AFFILIATED USERS
                        </Typography>
                    </Grid>
                    <Grid container>
                        <ACGNumberUsersAgGrid
                            data={acgUsers}
                        />
                    </Grid>
                </Grid> */}
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}